import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { MapUtils } from '../../utility/map-utils.js';
import { OrderSideEffect } from './orders/order-side-effect.js';
import { DeferOrderCompletionSideEffect } from './orders/defer-order-completion-side-effect.js';
import { SendOnlyPaymentConfirmationSideEffect } from './orders/send-only-payment-confirmation-side-effect.js';
import { CreateOpenAccessPassFromTicketSideEffect } from './orders/create-open-access-pass-from-ticket-side-effect.js';
import { RedirectAfterPaymentSideEffect } from './orders/redirect-after-payment-side-effect.js';
import { SendProductOrderEmailSideEffect } from './orders/send-product-order-email-side-effect.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';
import { CreateTicketGiftCardSideEffect } from './orders/create-ticket-gift-card-side-effect.js';
import { CreateOpenTicketPassSideEffect } from './orders/create-open-ticket-pass-side-effect.js';
import { CreateSingleOpenAccessPassSideEffect } from './orders/create-single-open-access-pass-side-effect.js';

/**
 * @deprecated Use `OrderSideEffect' instead.
 */
export enum LegacyOrderHookId {
  DeferOrderCompletion = 'defer-order-completion',
  SendOnlyPaymentConfirmation = 'send-only-payment-confirmation',
  CreateGiftCard = 'create-gift-card',
  // Historical names for an open ticket pass.
  CreateSeasonPass = 'create-season-pass',
  CreateOpenPass = 'create-open-pass',
  CreateOpenTicketPass = 'create-open-ticket-pass',
  CreateOpenAccessPass = 'create-open-access-pass',
  CreateOpenAccessPassFromTicket = 'create-open-access-pass-from-ticket',
  RedirectAfterPayment = 'redirect-after-payment',
  SendProductOrderConfirmationEmail = 'send-product-order-confirmation-email',

  // DEPRECATED
  CreateOpenAccessPassPlaceholder = 'create-open-access-pass-placeholder',
}

/**
 * @deprecated Use `OrderSideEffect' instead.
 */
export class LegacyOrderHook extends DataModel {
  static readonly TYPE_ID: string = 'adf17d290a074e3686ff69917664c86b';

  private constructor(
    public readonly uniqueId: string,
    private readonly fields: SerializedData,
  ) {
    super();
  }

  get $typeId(): string {
    return LegacyOrderHook.TYPE_ID;
  }

  static create(uniqueId: string, fields?: SerializedData): LegacyOrderHook {
    return new LegacyOrderHook(uniqueId, fields ?? {});
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyOrderHook {
    return new LegacyOrderHook(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<SerializedData>(map, 'fields'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'fields': this.fields,
    };
  }

  clone(): this {
    return new LegacyOrderHook(
        this.uniqueId,
        this.fields,
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.fields === other.fields;
  }

  setString(key: string, value: string): void {
    this.fields[key] = value;
  }

  setDouble(key: string, value: number): void {
    this.fields[key] = value;
  }

  setBoolean(key: string, value: boolean): void {
    this.fields[key] = value;
  }

  getStringOrNull(key: string): string | null {
    return MapUtils.getOrNull<string>(this.fields, key);
  }

  private getString(key: string): string {
    const value = MapUtils.getOrNull<string>(this.fields, key);
    if(value == null) {
      throw new SerializationException(`Missing or null field with key {${key}} in hook: ${JSON.stringify(this.fields)}`);
    }

    return value;
  }

  getDoubleOrNull(key: string): number | null {
    return MapUtils.getOrNull<number>(this.fields, key);
  }

  // private getDouble(key: string): number {
  //   const value = MapUtils.getOrNull<number>(this.fields, key);
  //   if(value == null) {
  //     throw new SerializationException(`Missing or null field with key {${key}} in hook: ${JSON.stringify(this.fields)}`);
  //   }

  //   return value;
  // }

  getBooleanOrNull(key: string): boolean | null {
    return MapUtils.getOrNull<boolean>(this.fields, key);
  }

  private getBoolean(key: string): boolean {
    const value = MapUtils.getOrNull<boolean>(this.fields, key);
    if(value == null) {
      throw new SerializationException(`Missing or null field with key {${key}} in hook: ${JSON.stringify(this.fields)}`);
    }

    return value;
  }

  toSideEffect(): OrderSideEffect | null {
    switch(this.uniqueId) {
      case LegacyOrderHookId.DeferOrderCompletion:
        return DeferOrderCompletionSideEffect.create();
      case LegacyOrderHookId.SendOnlyPaymentConfirmation:
        return SendOnlyPaymentConfirmationSideEffect.create();
      case LegacyOrderHookId.CreateGiftCard:
        return CreateTicketGiftCardSideEffect.create();
      case LegacyOrderHookId.CreateSeasonPass:
      case LegacyOrderHookId.CreateOpenPass:
      case LegacyOrderHookId.CreateOpenTicketPass:
        return CreateOpenTicketPassSideEffect.create();
      case LegacyOrderHookId.CreateOpenAccessPass:
        return CreateSingleOpenAccessPassSideEffect.create();
      case LegacyOrderHookId.CreateOpenAccessPassFromTicket:
        return CreateOpenAccessPassFromTicketSideEffect.legacy();
      case LegacyOrderHookId.RedirectAfterPayment:
        return RedirectAfterPaymentSideEffect.create({
          webRoute: this.getString('webRoute'),
          isEvent: this.getBoolean('isEvent'),
        });
      case LegacyOrderHookId.CreateOpenAccessPassPlaceholder:
        // This hook is no longer supported. Theoretically there shouldn't be any active orders using this.
        return null;
      case LegacyOrderHookId.SendProductOrderConfirmationEmail:
      {
        const eventId = this.getStringOrNull('eventId');
        return eventId != null ?
            SendProductOrderEmailSideEffect.create({ eventId, emailTemplateId: eventId }):
            SendProductOrderEmailSideEffect.default({ eventId });
      }
    }

    throw new SerializationException(`Cannot convert legacy order hook of type {${this.uniqueId}} to order side effect.`);
  }
}

export class LegacyOrderHookBuilder extends DataModelBuilder<LegacyOrderHook> {
  build(context: SerializationContext, map: SerializedData): LegacyOrderHook {
    return LegacyOrderHook.fromMap(context, map);
  }
}
